import { useEffect } from "react";

import { DotLottieReact } from "@lottiefiles/dotlottie-react";

import { Box, Center, Text } from "@chakra-ui/react";

import { dotLoaderJson, useDotLoadingState } from "@euka-mfes/bark";

export interface DotLoaderPropTypes {
  size?: "sm" | "md" | "lg";
  loadingText?: string;
  overlayColor?: string;
  overlayStyle?: React.CSSProperties;
  textGap?: number;
  textColor?: string;
  isLoaded?: boolean;
  onLoadComplete?: () => void;
  minDisplayTime?: number;
  src?: string;
  direction?: "column" | "row";
}

const sizes = {
  sm: 0.35,
  md: 0.4,
  lg: 0.6
};

const DotLoader = ({
  size = "sm",
  loadingText,
  overlayColor,
  overlayStyle,
  textGap = 8,
  textColor,
  isLoaded = false,
  onLoadComplete,
  minDisplayTime = 1000,
  direction = "column",
  src = dotLoaderJson
}: DotLoaderPropTypes) => {
  const scale = sizes[size] || sizes.md;
  const showLoader = useDotLoadingState({
    loading: !isLoaded,
    minDisplayTime
  });

  useEffect(() => {
    if (!showLoader) {
      onLoadComplete?.();
    }
  }, [showLoader, onLoadComplete]);

  if (!showLoader) return null;

  return (
    <Box
      data-testid="dot-loader-overlay"
      position={direction === "column" ? "absolute" : "relative"}
      top="0"
      left="0"
      right="0"
      bottom="0"
      backgroundColor={overlayColor || "transparent"}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      zIndex="overlay"
      style={overlayStyle}
    >
      <Center
        flexDirection="column"
        gap={0}
        justifyContent="center"
        alignItems="center"
        mt={-2}
      >
        <Box h={direction === "row" ? undefined : textGap}>
          <DotLottieReact
            autoplay
            loop
            backgroundColor="transparent"
            src={src}
            style={{
              height: `${scale * 100}px`,
              width: `${scale * 100}px`
            }}
          />
        </Box>
        {loadingText && (
          <Text
            mt={0}
            textStyle="body.small"
            color={textColor || "text.DEFAULT"}
          >
            {loadingText}
          </Text>
        )}
      </Center>
    </Box>
  );
};

export default DotLoader;
